import { createModel } from '@rematch/core';
import type { RootModel } from '@/components/WellLoggingOperations/types';

import type { TabletSettings, TabletSettingsState } from '@/components/WellLoggingOperations/types';
import {
    defaultColumnsVisibility,
    defaultColumnsSizing,
    defaultColumnsOrder,
    defaultRigisColumnsOrder,
    defaultRigisColumnsCount,
} from './config';

/*  !! Здесь нельзя использовать WellId
    потому что одна скважина может быть в нескольких списках с разными настройками
*/
type WellKey = string;

interface PayloadByWellKey<T> {
    readonly wellKey: WellKey;
    readonly data: T;
}

export const TABLET_SETTINGS_KEYS = 'tabletSettings';
export const PROFILE_SETTINGS_KEY = 'profileSettings';

const getCashedState = (): TabletSettingsState => {
    try {
        if (typeof window !== 'undefined') {
            const item = localStorage?.getItem(TABLET_SETTINGS_KEYS);
            return item ? (JSON.parse(item) as TabletSettingsState) : {};
        }
        return {};
    } catch (error) {
        return {};
    }
};

export const defaultTabletSettings: TabletSettings = {
    wellLoggingKeys: [],
    curveSettings: {},
    curveDisplaySettings: {},
    columnsVisibility: defaultColumnsVisibility,
    columnsSizing: defaultColumnsSizing,
    columnsOrder: defaultColumnsOrder,
    rigisColumnsCount: defaultRigisColumnsCount,
    rigisColumnsOrder: defaultRigisColumnsOrder,
    rigisParamsHistogramIds: [],
    fgiParamsHistogramIds: [],
    coreKeys: [],
    headerSizes: {
        rigisColumnHeight: 100,
        height: 143,
        minHeight: 0,
        maxHeight: 600,
    },
    // поля для оптимизации
    prevTemplateId: null,
    isKeysInit: false,
    isFirstLoad: false,
};

export const tabletSettingsModel = createModel<RootModel>()({
    name: 'tabletSettings',
    state: getCashedState(),
    reducers: {
        setTabletSettings(state, payload: PayloadByWellKey<Partial<TabletSettings>>) {
            const settings = state[payload.wellKey] ?? defaultTabletSettings;
            return { ...state, [payload.wellKey]: { ...settings, ...payload.data } };
        },
        setBatchTabletSettings(state, payload: Array<PayloadByWellKey<TabletSettings>>) {
            return payload.reduce((res, { wellKey, data }) => ({ ...res, [wellKey]: { ...data } }), { ...state });
        },
        setColumnSize(state, payload: { wellKey?: number; columnSize: number; columnTitle: string }) {
            if (payload.wellKey) {
                const settings = state[payload.wellKey] ?? defaultTabletSettings;
                return {
                    ...state,
                    [payload.wellKey]: {
                        ...settings,
                        columnsSizing: {
                            ...settings.columnsSizing,
                            [payload.columnTitle]: payload.columnSize,
                        },
                    },
                };
            } else {
                const newState: TabletSettingsState = {};
                for (const wellId of Object.keys(state)) {
                    const settings = state[wellId];
                    newState[wellId] = {
                        ...settings,
                        columnsSizing: {
                            ...settings.columnsSizing,
                            [payload.columnTitle]: payload.columnSize,
                        },
                    };
                }
                return newState;
            }
        },
        setColumnVisibility(state, payload: { wellKey: number; columnTitle: string }) {
            const settings = state[payload.wellKey] ?? defaultTabletSettings;
            return {
                ...state,
                [payload.wellKey]: {
                    ...settings,
                    columnsVisibility: {
                        ...settings.columnsVisibility,
                        [payload.columnTitle]: true,
                    },
                },
            };
        },
        updateHistogramIds(
            state,
            payload: {
                wellKey: string;
                versionId: number;
                type: 'rigisParamsHistogramIds' | 'fgiParamsHistogramIds';
                ids: number[];
            },
        ) {
            const settings = state[payload.wellKey] ?? defaultTabletSettings;
            return {
                ...state,
                [payload.wellKey]: {
                    ...settings,
                    [payload.type]: {
                        ...settings[payload.type],
                        [payload.versionId]: payload.ids,
                    },
                },
            };
        },
    },
});
